import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { FileText, X, CheckCircle2, AlertTriangle, Users, ClipboardCheck, ScrollText, Fingerprint } from "lucide-react";
import Panel from "./Panel";
import ExportMenu from "./ExportMenu";

/**
 * Audit Working Papers — SA 230-style digital working papers with
 * assertion, methodology, evidence, sampling, third-party confirmation,
 * and auditor's conclusion. Each WP is independently downloadable.
 */
export default function WorkingPapersPanel() {
    const [wps, setWps] = useState(null);
    const [selected, setSelected] = useState(null);
    const [areaFilter, setAreaFilter] = useState("All");

    useEffect(() => {
        api.get("/audit/workpapers").then((r) => setWps(r.data.working_papers));
    }, []);

    if (!wps) {
        return (
            <Panel testId="au-wp-loading" title="Digital Working Papers" subtitle="Loading auditor's evidence file...">
                <div className="text-xs font-mono-fin text-[var(--text-secondary)] py-6 text-center uppercase tracking-wider">// LOADING WORKING PAPERS...</div>
            </Panel>
        );
    }

    const areas = ["All", ...Array.from(new Set(wps.map((w) => w.area)))];
    const filtered = wps.filter((w) => areaFilter === "All" || w.area === areaFilter);

    return (
        <>
            <Panel
                testId="au-workpapers"
                title="Auditor's Digital Working Papers"
                subtitle={`${wps.length} SA-230 working papers · Prepared, reviewed & signed · Board-pack ready`}
                right={<ExportMenu kind="audit" label="Full Audit File" testId="export-audit-file" />}
            >
                <div className="flex items-center gap-2 font-mono-fin text-[10px] uppercase tracking-wider flex-wrap mb-3">
                    <span className="text-[var(--text-secondary)]">Filter:</span>
                    {areas.map((a) => (
                        <button
                            key={a}
                            data-testid={`wp-area-${a.replace(/\s|&/g, "").toLowerCase()}`}
                            onClick={() => setAreaFilter(a)}
                            className={`px-2.5 py-1 border transition-colors ${
                                areaFilter === a
                                    ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--text-primary)]"
                                    : "border-panel text-[var(--text-secondary)] hover:border-[var(--brand-amber)]"
                            }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filtered.map((w) => {
                        const isFlag = w.status !== "PASS";
                        return (
                            <button
                                key={w.wp_id}
                                data-testid={`wp-card-${w.wp_id}`}
                                onClick={() => setSelected(w)}
                                className={`text-left border p-3 hover:border-[var(--brand-amber)] hover:bg-[var(--bg-panel-hover)] transition-colors group ${
                                    isFlag ? "border-neg/40 bg-[rgba(255,67,61,0.04)]" : "border-panel"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-1.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)] shrink-0">{w.wp_id}</div>
                                        <div className="text-[9px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 border border-panel text-[var(--text-secondary)]">{w.area}</div>
                                    </div>
                                    {isFlag
                                        ? <AlertTriangle size={13} className="text-neg shrink-0" />
                                        : <CheckCircle2 size={13} className="text-pos shrink-0" />}
                                </div>
                                <div className="text-xs text-[var(--text-primary)] font-body group-hover:text-[var(--brand-amber)] transition-colors leading-snug mb-1.5">
                                    {w.check_name}
                                </div>
                                <div className="text-[10px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider truncate">
                                    Assertion: <span className="text-[var(--text-secondary)]">{w.assertion.split("·")[0].trim()}</span>
                                </div>
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-panel">
                                    <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                                        {w.samples?.length || 0} samples · {w.evidence?.length || 0} evidence
                                    </div>
                                    <div className="text-[9px] font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)] flex items-center gap-1">
                                        View <FileText size={10} />
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </Panel>

            {selected && <WorkingPaperDrawer wp={selected} onClose={() => setSelected(null)} />}
        </>
    );
}

function WorkingPaperDrawer({ wp, onClose }) {
    const isFlag = wp.status !== "PASS";
    return (
        <div className="fixed inset-0 z-40" data-testid={`wp-drawer-${wp.wp_id}`}>
            <div className="absolute inset-0 bg-[var(--bg-base)]/70 backdrop-blur-sm" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[var(--bg-panel)] border-l-2 border-[var(--brand-amber)] shadow-2xl overflow-y-auto">
                <div className="sticky top-0 bg-[var(--bg-panel)] border-b border-panel px-5 py-4 flex items-start justify-between gap-4 z-10">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.25em] text-[var(--brand-amber)]">// WORKING PAPER {wp.wp_id}</div>
                            <div className={`text-[9px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 ${
                                isFlag ? "bg-neg/20 text-neg" : "bg-pos/20 text-pos"
                            }`}>{wp.status}</div>
                        </div>
                        <div className="font-display font-bold text-lg text-[var(--text-primary)] leading-tight">{wp.check_name}</div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-0.5">
                            Area: {wp.area} · Prepared {wp.date}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <ExportMenu kind={`workpaper/${wp.wp_id}`} label="Download" testId={`wp-export-${wp.wp_id}`} />
                        <button
                            data-testid={`wp-close-${wp.wp_id}`}
                            onClick={onClose}
                            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 border border-panel hover:border-[var(--brand-amber)]"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>

                <div className="p-5 space-y-5">
                    <Section icon={ClipboardCheck} title="Result">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-mono-fin">
                            <KV label="Expected" value={wp.expected} />
                            <KV label="Actual" value={wp.actual} accent={isFlag ? "amber" : "pos"} />
                            <KV label="Status" value={wp.status} accent={isFlag ? "neg" : "pos"} />
                        </div>
                        <div className="text-xs font-body text-[var(--text-secondary)] mt-3 leading-relaxed border-l-2 border-panel pl-3 italic">
                            {wp.comment}
                        </div>
                    </Section>

                    <Section icon={Fingerprint} title="Assertion Tested">
                        <div className="text-xs font-body text-[var(--text-primary)] bg-[var(--bg-panel-hover)] p-3 border border-panel">
                            {wp.assertion}
                        </div>
                    </Section>

                    <Section icon={ScrollText} title="Methodology">
                        <div className="text-xs font-body text-[var(--text-secondary)] leading-relaxed whitespace-pre-line bg-[var(--bg-panel-hover)] p-3 border border-panel">
                            {wp.methodology}
                        </div>
                    </Section>

                    {wp.evidence?.length > 0 && (
                        <Section icon={ClipboardCheck} title={`Evidence · Independent Recompute (${wp.evidence.length})`}>
                            <div className="border border-panel">
                                {wp.evidence.map((e, i) => (
                                    <div key={i} className={`flex items-center justify-between px-3 py-2 text-xs font-mono-fin ${i % 2 === 0 ? "bg-[var(--bg-panel-hover)]" : ""}`}>
                                        <span className="text-[var(--text-secondary)] pr-2">{e.desc}</span>
                                        <span className="text-[var(--text-primary)] font-semibold">{e.value}</span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    {wp.samples?.length > 0 && (
                        <Section icon={Users} title={`Sampled Vouchers (${wp.samples.length})`}>
                            <div className="border border-panel overflow-x-auto">
                                <table className="w-full text-[11px] font-mono-fin">
                                    <thead className="bg-[var(--brand-amber-dim)] text-[var(--brand-amber)]">
                                        <tr>
                                            {Object.keys(wp.samples[0]).map((k) => (
                                                <th key={k} className="text-left px-2.5 py-1.5 uppercase tracking-wider text-[9px] border-b border-panel">{k.replace(/_/g, " ")}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {wp.samples.map((s, i) => (
                                            <tr key={i} className={i % 2 === 0 ? "bg-[var(--bg-panel-hover)]" : ""}>
                                                {Object.values(s).map((v, j) => (
                                                    <td key={j} className="px-2.5 py-1.5 text-[var(--text-primary)] border-b border-panel">{String(v)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    )}

                    <Section icon={Users} title="Third-Party Confirmations (SA 505)">
                        <div className="text-xs font-body text-[var(--text-secondary)] leading-relaxed bg-[var(--bg-panel-hover)] p-3 border border-panel">
                            {wp.third_party}
                        </div>
                    </Section>

                    <Section icon={CheckCircle2} title="Auditor's Conclusion">
                        <div className={`text-xs font-body leading-relaxed p-3 border-l-4 ${
                            isFlag ? "border-neg bg-[rgba(255,67,61,0.06)] text-[var(--text-primary)]" : "border-pos bg-[rgba(74,246,195,0.06)] text-[var(--text-primary)]"
                        }`}>
                            {wp.conclusion}
                        </div>
                    </Section>

                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-panel">
                        <SignBlock label="Prepared By" name={wp.prepared_by} date={wp.date} />
                        <SignBlock label="Reviewed By" name={wp.reviewed_by} date={wp.date} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={13} className="text-[var(--brand-amber)]" />
                <div className="text-[10px] font-mono-fin uppercase tracking-[0.2em] text-[var(--brand-amber)]">{title}</div>
            </div>
            {children}
        </div>
    );
}

function KV({ label, value, accent }) {
    const color = accent === "pos" ? "text-pos" : accent === "neg" ? "text-neg" : accent === "amber" ? "text-amber" : "text-[var(--text-primary)]";
    return (
        <div className="border border-panel p-2.5 bg-[var(--bg-panel-hover)]">
            <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
            <div className={`text-xs mt-1 font-semibold ${color}`}>{value}</div>
        </div>
    );
}

function SignBlock({ label, name, date }) {
    return (
        <div className="border border-panel p-3 bg-[var(--bg-panel-hover)]">
            <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
            <div className="text-xs text-[var(--text-primary)] font-body mt-1">{name}</div>
            <div className="text-[9px] font-mono-fin text-[var(--text-secondary)] mt-1">Date: {date}</div>
        </div>
    );
}
